// orderPlacementGuard — the rules applied BEFORE a new order is created.
//
// Two separate protections, both about money we would otherwise lose:
//
//  1. Do Not Ship. A customer flagged for repeated COD returns costs us the
//     courier charge both ways every time. Until now the block only applied
//     when the parcel reached a courier desk — by which point the order had
//     been taken, stock reserved and the customer told it was coming. The
//     block now applies at ORDER PLACEMENT, and only a manager and above may
//     override it. It applies to prepaid too: a serial returner who prepays
//     still costs us the return leg and the refund.
//
//  2. Repeat-order warning. Ordering again days after the last order is
//     usually a duplicate, a confused customer, or a re-order the agent did
//     not realise was already on its way.

export const OVERRIDE_ROLES = new Set(['admin', 'super_admin', 'manager']);

/** Manager and above — the only roles that may place an order for a flagged customer. */
export const canOverrideDoNotShip = (role) => OVERRIDE_ROLES.has(String(role || '').toLowerCase());

/**
 * May this user place an order for this customer?
 *
 * @param {object|null} dnsFlag  the Do-Not-Ship index entry (falsy = not flagged)
 * @param {string} role          the current user's role
 * @returns {{allowed:boolean, blocked:boolean, canOverride:boolean, reason:string}}
 */
export const canPlaceOrderFor = (dnsFlag, role) => {
    if (!dnsFlag) return { allowed: true, blocked: false, canOverride: false, reason: '' };
    const canOverride = canOverrideDoNotShip(role);
    const why = String(dnsFlag.reason || dnsFlag.note || 'Repeated COD return').trim();
    return {
        allowed: canOverride,
        blocked: true,
        canOverride,
        reason: canOverride
            ? `This customer is marked DO NOT SHIP — ${why}. You may override this, and it will be recorded against your name.`
            : `This customer is marked DO NOT SHIP — ${why}. Only a manager can place an order for them.`,
    };
};

const DAY_MS = 24 * 60 * 60 * 1000;

/** Whole days between a past date and now. Returns null when the date is unusable. */
export const daysSince = (value, now = Date.now()) => {
    if (value == null || value === '') return null;
    const t = typeof value === 'number' ? value : Date.parse(value);
    if (!Number.isFinite(t)) return null;
    const d = Math.floor((now - t) / DAY_MS);
    return d < 0 ? 0 : d;                      // a future date is "today", never negative
};

/**
 * How loudly to warn that this customer ordered recently.
 *   0–7 days   → 'high'  (almost certainly a duplicate)
 *   8–14 days  → 'warn'
 *   15+ / none → 'none'
 */
export const repeatOrderWarning = (lastOrderDate, now = Date.now()) => {
    const days = daysSince(lastOrderDate, now);
    if (days === null) return { level: 'none', days: null, label: '' };
    if (days <= 7) {
        return {
            level: 'high', days,
            label: days === 0 ? 'ordered TODAY' : days === 1 ? 'ordered YESTERDAY' : `ordered ${days} days ago`,
        };
    }
    if (days <= 14) return { level: 'warn', days, label: `ordered ${days} days ago` };
    return { level: 'none', days, label: '' };
};

/** One line describing the previous order, for the warning banner. */
export const lastOrderSummary = (order) => {
    if (!order) return '';
    const id = order.id || order.orderId || '';
    const amount = Number(order.amount ?? order.total ?? order.payment?.total);
    const status = order.status || order.orderStatus || '';
    const mode = order.paymentMode || order.payment?.mode || '';
    return [
        id && `#${id}`,
        Number.isFinite(amount) && amount > 0 && `₹${Math.round(amount)}`,
        mode,
        status && `· ${status}`,
    ].filter(Boolean).join(' ');
};
